import React from 'react';
import { Calendar, Clock, MessageSquare, MapPin, Shield, Lightbulb, Database, Bell, Users, Settings } from 'lucide-react';

export type ViewType = 'schedule' | 'timeline' | 'messages' | 'tracking' | 'compliance' | 'suggestions' | 'data' | 'notifications' | 'suppliers' | 'settings';

interface NavigationBarProps {
  activeView: ViewType;
  onViewChange: (view: ViewType) => void;
  badges?: {
    messages?: number;
    notifications?: number;
    suggestions?: number;
    tracking?: { wrongSite: number };
    compliance?: { issues: number };
  };
}

export const NavigationBar: React.FC<NavigationBarProps> = ({ 
  activeView, 
  onViewChange, 
  badges = {} 
}) => {
  const navItems: Array<{
    id: ViewType;
    label: string;
    icon: React.ReactNode;
    shortcut?: string;
    badge?: number | { text: string; color: string };
    primary?: boolean;
  }> = [
    {
      id: 'schedule',
      label: 'Schedule',
      icon: <Calendar className="w-3 h-3" />,
      shortcut: '⌘1',
      primary: true
    },
    {
      id: 'timeline',
      label: 'Timeline',
      icon: <Clock className="w-3 h-3" />,
      shortcut: '⌘2',
      primary: true
    },
    {
      id: 'messages',
      label: 'Command Centre',
      icon: <MessageSquare className="w-3 h-3" />,
      shortcut: '⌘3',
      badge: badges.messages,
      primary: true
    },
    {
      id: 'tracking',
      label: 'Tracking',
      icon: <MapPin className="w-3 h-3" />,
      shortcut: '⌘4',
      badge: badges.tracking?.wrongSite ? { text: `${badges.tracking.wrongSite}!`, color: 'red' } : undefined,
      primary: true
    },
    {
      id: 'suggestions',
      label: 'Suggestions',
      icon: <Lightbulb className="w-3 h-3" />,
      shortcut: '⌘5',
      badge: badges.suggestions,
      primary: true
    },
    {
      id: 'data',
      label: 'Data Sources',
      icon: <Database className="w-3 h-3" />,
      shortcut: '⌘6',
      primary: true
    },
    {
      id: 'compliance',
      label: 'Compliance',
      icon: <Shield className="w-3 h-3" />,
      shortcut: '⌘7',
      badge: badges.compliance?.issues,
      primary: true
    }
  ];

  const secondaryItems: Array<{
    id: ViewType;
    label: string;
    icon: React.ReactNode;
    badge?: number;
  }> = [
    {
      id: 'notifications',
      label: 'Notifications',
      icon: <Bell className="w-3 h-3" />,
      badge: badges.notifications
    },
    {
      id: 'suppliers',
      label: 'Suppliers',
      icon: <Users className="w-3 h-3" />
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <Settings className="w-3 h-3" />
    }
  ];

  const NavButton: React.FC<{
    item: typeof navItems[0] | typeof secondaryItems[0];
    isActive: boolean;
    onClick: () => void;
  }> = ({ item, isActive, onClick }) => {
    const getBadgeColor = (badge: any) => {
      if (typeof badge === 'object' && badge.color === 'red') return '#d93025';
      return '#1a73e8';
    };

    const getBadgeText = (badge: any) => {
      if (typeof badge === 'object') return badge.text;
      return badge;
    };

    return (
      <button
        onClick={onClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '4px 8px',
          backgroundColor: isActive ? '#ffffff' : 'transparent',
          border: 'none',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: isActive ? 500 : 400,
          color: isActive ? '#1a73e8' : '#5f6368',
          cursor: 'pointer',
          transition: 'all 0.2s',
          position: 'relative',
          whiteSpace: 'nowrap',
          boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.08)' : 'none'
        }}
        onMouseEnter={(e) => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.5)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
        title={'shortcut' in item ? `${item.label} (${item.shortcut})` : item.label}
      >
        <span style={{ display: 'flex', alignItems: 'center' }}>{item.icon}</span>
        <span>{item.label}</span>
        
        {item.badge && (
          <span style={{
            marginLeft: '2px',
            padding: '1px 4px',
            backgroundColor: getBadgeColor(item.badge),
            color: 'white',
            borderRadius: '8px',
            fontSize: '9px',
            fontWeight: 600,
            minWidth: '14px',
            textAlign: 'center'
          }}>
            {getBadgeText(item.badge)}
          </span>
        )}
      </button>
    );
  };

  return (
    <div style={{
      backgroundColor: '#fff',
      borderBottom: '1px solid #e8eaed',
      padding: '0 20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Event Info Bar */}
      <div style={{
        padding: '10px 0',
        borderBottom: '1px solid #e8eaed',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '12px',
          color: '#5f6368'
        }}>
          <span style={{ fontWeight: 500, color: '#202124', fontSize: '14px' }}>
            Sarah & James Wedding
          </span>
          <span style={{ color: '#5f6368', fontSize: '10px' }}>•</span>
          <span style={{ fontSize: '11px' }}>December 15, 2024</span>
          <span style={{ color: '#5f6368', fontSize: '10px' }}>•</span>
          <span style={{ fontSize: '11px' }}>Grand Ballroom, The Ritz Carlton</span>
        </div>
        
        {/* Secondary Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {secondaryItems.map(item => (
            <NavButton
              key={item.id}
              item={item}
              isActive={activeView === item.id}
              onClick={() => onViewChange(item.id)}
            />
          ))}
        </div>
      </div>

      {/* Primary Navigation - Grouped with Pills */}
      <div style={{
        padding: '8px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: '11px',
        flexWrap: 'nowrap'
      }}>
        {/* PLAN Group - Blue Theme */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          background: 'linear-gradient(135deg, #e3f2fd 0%, #e8f0fe 100%)',
          borderRadius: '18px',
          padding: '3px 4px 3px 8px',
          border: '1px solid #c5cae9',
          flexShrink: 0
        }}>
          <span style={{ 
            color: '#1565c0', 
            fontSize: '10px', 
            fontWeight: 700,
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
            marginRight: '6px'
          }}>
            Plan
          </span>
          <div style={{ 
            background: 'rgba(255,255,255,0.8)',
            borderRadius: '14px',
            padding: '1px',
            display: 'flex',
            gap: '1px',
            backdropFilter: 'blur(10px)'
          }}>
            <NavButton
              item={navItems.find(i => i.id === 'schedule')!}
              isActive={activeView === 'schedule'}
              onClick={() => onViewChange('schedule')}
            />
            <NavButton
              item={navItems.find(i => i.id === 'timeline')!}
              isActive={activeView === 'timeline'}
              onClick={() => onViewChange('timeline')}
            />
          </div>
        </div>

        {/* RUN Group - Green Theme */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)',
          borderRadius: '18px',
          padding: '3px 4px 3px 8px',
          border: '1px solid #aed581',
          flexShrink: 0,
          margin: '0 20px'
        }}>
          <span style={{ 
            color: '#2e7d32', 
            fontSize: '10px', 
            fontWeight: 700,
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
            marginRight: '6px'
          }}>
            Run
          </span>
          <div style={{ 
            background: 'rgba(255,255,255,0.8)',
            borderRadius: '14px',
            padding: '1px',
            display: 'flex',
            gap: '1px',
            backdropFilter: 'blur(10px)'
          }}>
            <NavButton
              item={navItems.find(i => i.id === 'messages')!}
              isActive={activeView === 'messages'}
              onClick={() => onViewChange('messages')}
            />
            <NavButton
              item={navItems.find(i => i.id === 'tracking')!}
              isActive={activeView === 'tracking'}
              onClick={() => onViewChange('tracking')}
            />
            <NavButton
              item={navItems.find(i => i.id === 'suggestions')!}
              isActive={activeView === 'suggestions'}
              onClick={() => onViewChange('suggestions')}
            />
          </div>
        </div>

        {/* RESOURCES Group - Purple Theme */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          background: 'linear-gradient(135deg, #f3e5f5 0%, #fce4ec 100%)',
          borderRadius: '18px',
          padding: '3px 4px 3px 8px',
          border: '1px solid #ce93d8',
          flexShrink: 0
        }}>
          <span style={{ 
            color: '#6a1b9a', 
            fontSize: '10px', 
            fontWeight: 700,
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
            marginRight: '6px'
          }}>
            Resources
          </span>
          <div style={{ 
            background: 'rgba(255,255,255,0.8)',
            borderRadius: '14px',
            padding: '1px',
            display: 'flex',
            gap: '1px',
            backdropFilter: 'blur(10px)'
          }}>
            <NavButton
              item={navItems.find(i => i.id === 'compliance')!}
              isActive={activeView === 'compliance'}
              onClick={() => onViewChange('compliance')}
            />
            <NavButton
              item={navItems.find(i => i.id === 'data')!}
              isActive={activeView === 'data'}
              onClick={() => onViewChange('data')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};